import { Alert, Button, Form } from "antd";
import BaseModalForm from "components/BaseModalForm";
import message from "components/CustomMessage";
import { isNil } from "lodash";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FieldTypes, nameRegexValidator } from "utils/formUtils";
import { duplicateNameValidator } from "utils/validatorUtil";

import {
  AWS_REGIONS,
  ZscalerCloudFormationSetupInfo,
  arnValidator,
  bucketNameValidator,
} from "../zscaler/zscalerConstants";
import {
  SentinelOneS3API,
  SentinelOneS3Config,
} from "./sentinelOneLogsConstants";

interface SentinelOneS3ConfigFormProps {
  configData?: SentinelOneS3Config;
  onUpdateConfigComponent: () => void;
  configList?: SentinelOneS3Config[];
  afterClose?: () => void;
}

export default forwardRef(function SentinelOneS3ConfigForm(
  {
    configData,
    afterClose,
    configList = [],
    onUpdateConfigComponent,
  }: SentinelOneS3ConfigFormProps,
  ref,
) {
  const [configId, setConfigId] = useState(configData?.id);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [setupInfo, setSetupInfo] =
    useState<ZscalerCloudFormationSetupInfo | null>(null);
  const isEdit = !isNil(configData?.id);

  useEffect(() => {
    if (isEdit) return;
    const { promise, cancel } = SentinelOneS3API.getCloudFormationTemplate();
    promise.then((response: any) => setSetupInfo(response?.data || response));
    return cancel;
  }, [isEdit]);

  const filteredConfigList = useMemo(() => {
    if (!configData?.id) return configList;
    return configList.filter((item) => item.id !== configData.id);
  }, [configList, configData?.id]);

  const initialValues = useMemo(() => {
    if (!configData) return undefined;
    const { authConfig, configProperties, ...rest } = configData as any;
    return { ...rest, ...authConfig, ...configProperties };
  }, [configData]);

  useEffect(() => {
    if (isEdit || !setupInfo?.prefilledParameters) return;
    const { ExternalId, TenantUUID } = setupInfo.prefilledParameters;
    form.setFieldsValue({
      ...(ExternalId && { externalId: ExternalId }),
      ...(TenantUUID && { uuid: TenantUUID }),
    });
  }, [setupInfo, form, isEdit]);

  const onIAMClick = useCallback(() => {
    if (!setupInfo?.quickCreateUrl) {
      message.error("Setup information not available. Please try again later.");
      return;
    }
    window.open(setupInfo.quickCreateUrl, "_blank", "noopener,noreferrer");
  }, [setupInfo]);

  const saveConfiguration = useCallback(async () => {
    try {
      setLoading(true);
      const {
        name,
        roleArn,
        externalId,
        uuid,
        bucketName,
        bucketRegion,
        prefix,
      } = form.getFieldsValue();
      const data = {
        name,
        uuid,
        status: "ACTIVE",
        authType: "IAM_ROLE",
        authConfig: { roleArn, externalId },
        configProperties: {
          bucketName,
          bucketRegion,
          ...(prefix ? { prefix } : {}),
        },
      };
      if (configId) {
        await SentinelOneS3API.updateConfiguration(configId, data).promise;
        message.success("Configuration updated successfully");
      } else {
        const response =
          await SentinelOneS3API.createConfiguration(data).promise;
        setConfigId(response.id);
        message.success("Configuration created successfully");
      }
      onUpdateConfigComponent();
    } finally {
      setLoading(false);
    }
  }, [form, configId, onUpdateConfigComponent]);

  const prependFields = useMemo(
    () => [
      {
        render: () => (
          <Alert
            type="warning"
            className="m-b-xs"
            message="AWS CloudFormation Setup Required"
            description="Click on Create IAM Role button to create a role in your AWS account which will allow ArmorCode to pull SentinelOne logs from your S3 bucket. Using CloudFormation, we will create the necessary IAM role and SNS topic. After the stack completes, navigate to the Outputs section and copy the Role ARN value."
          />
        ),
      },
      {
        render: () => (
          <Button
            className="m-t-xs m-b-xs"
            type="primary"
            onClick={onIAMClick}
            disabled={!setupInfo}
          >
            Create Resource
          </Button>
        ),
      },
    ],
    [onIAMClick, setupInfo],
  );

  const fields = useMemo(
    () => [
      ...prependFields,
      {
        name: "name",
        label: "Configuration Name",
        NoDefaultLengthRule: true,
        required: true,
        rules: [
          nameRegexValidator,
          duplicateNameValidator({ list: filteredConfigList }),
        ],
        props: {
          autoFocus: true,
          placeholder: "Enter a unique name for this configuration",
        },
      },
      {
        name: "roleArn",
        label: "Role ARN",
        required: true,
        rules: [arnValidator],
        props: {
          placeholder:
            "arn:aws:iam::123456789012:role/ArmorCodeSentinelOneRole",
        },
        help: "Copy from CloudFormation stack Outputs tab",
      },
      {
        name: "externalId",
        label: "External ID",
        required: true,
        props: { placeholder: "Auto-filled from setup" },
        help: "Unique identifier for secure cross-account access (auto-filled)",
      },
      {
        name: "uuid",
        label: "UUID",
        required: true,
        props: { placeholder: "Auto-filled from setup" },
      },
      {
        name: "bucketName",
        label: "S3 Bucket Name",
        required: true,
        rules: [bucketNameValidator],
        props: { placeholder: "my-sentinelone-logs-bucket" },
        help: "The name of the S3 bucket containing SentinelOne logs",
      },
      {
        name: "bucketRegion",
        label: "Bucket Region",
        type: FieldTypes.SELECT,
        required: true,
        options: AWS_REGIONS,
        props: {
          placeholder: "Select AWS region",
          showSearch: true,
          filterOption: (input: string, option: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase()),
        },
        help: "The AWS region where your S3 bucket is located",
      },
      {
        name: "prefix",
        label: "S3 Prefix (Optional)",
        required: false,
        props: { placeholder: "logs/sentinelone/" },
        help: "Optional path prefix within the bucket where logs are stored",
      },
    ],
    [prependFields, filteredConfigList],
  );

  return (
    <BaseModalForm
      title={
        isEdit ? "Edit SentinelOne Configuration" : "Configure SentinelOne"
      }
      ref={ref as React.Ref<any>}
      form={form}
      fields={fields}
      onOk={saveConfiguration}
      initialValues={initialValues}
      afterClose={afterClose}
      confirmLoading={loading}
      width={600}
    />
  );
});
